// ErrorTrackerService - Client-side JavaScript error tracking
//
// Captures:
// - console.error and console.warn calls
// - Unhandled exceptions
// - Unhandled promise rejections
// - Resource loading failures (images, scripts, etc.)
//
// Usage:
//   window.errorTracker.getErrors()      // Get all captured errors
//   window.errorTracker.getErrorSummary() // Get error summary
//   window.errorTracker.clearErrors()    // Clear all errors

export class ErrorTrackerService {
  constructor() {
    this.errors = []
    this.maxErrors = 50
    this.timeWindow = 30000 // 30 seconds
    this.originalConsoleError = console.error
    this.originalConsoleWarn = console.warn
    this.initialize()
  }

  initialize() {
    this.interceptConsoleErrors()
    this.interceptUnhandledErrors()
    this.interceptUnhandledRejections()
    this.interceptResourceErrors()
  }

  interceptConsoleErrors() {
    const self = this

    console.error = function(...args) {
      self.originalConsoleError.apply(console, args)
      self.captureError({
        type: 'console.error',
        message: args.map(arg => {
          if (typeof arg === 'object') {
            try {
              return JSON.stringify(arg)
            } catch (e) {
              return '[Circular Reference]'
            }
          }
          return String(arg)
        }).join(' '),
        timestamp: Date.now(),
        stack: new Error().stack
      })
    }

    console.warn = function(...args) {
      self.originalConsoleWarn.apply(console, args)
      self.captureError({
        type: 'console.warn',
        message: args.map(arg => {
          if (typeof arg === 'object') {
            try {
              return JSON.stringify(arg)
            } catch (e) {
              return '[Circular Reference]'
            }
          }
          return String(arg)
        }).join(' '),
        timestamp: Date.now(),
        stack: new Error().stack
      })
    }
  }

  interceptUnhandledErrors() {
    const self = this

    window.addEventListener('error', (event) => {
      self.captureError({
        type: 'unhandled_error',
        message: event.message,
        filename: event.filename,
        line: event.lineno,
        column: event.colno,
        stack: event.error?.stack,
        timestamp: Date.now()
      })
    })
  }

  interceptUnhandledRejections() {
    const self = this

    window.addEventListener('unhandledrejection', (event) => {
      self.captureError({
        type: 'unhandled_promise_rejection',
        message: event.reason?.message || String(event.reason),
        stack: event.reason?.stack,
        timestamp: Date.now()
      })
    })
  }

  interceptResourceErrors() {
    const self = this

    window.addEventListener('error', (event) => {
      if (event.target !== window) {
        self.captureError({
          type: 'resource_error',
          message: `Failed to load resource: ${event.target.src || event.target.href}`,
          element: event.target.tagName,
          source: event.target.src || event.target.href,
          timestamp: Date.now()
        })
      }
    }, true)
  }

  captureError(errorData) {
    this.errors.push({
      ...errorData,
      url: window.location.href,
      userAgent: navigator.userAgent,
      id: this.generateErrorId()
    })

    this.cleanupOldErrors()
    this.limitErrorCount()
  }

  cleanupOldErrors() {
    const cutoffTime = Date.now() - this.timeWindow
    this.errors = this.errors.filter(error => error.timestamp > cutoffTime)
  }

  limitErrorCount() {
    if (this.errors.length > this.maxErrors) {
      this.errors = this.errors.slice(-this.maxErrors)
    }
  }

  generateErrorId() {
    return Math.random().toString(36).substr(2, 9)
  }

  getErrors() {
    this.cleanupOldErrors()
    return [...this.errors]
  }

  getRecentErrors(timeWindow = this.timeWindow) {
    const cutoffTime = Date.now() - timeWindow
    return this.errors.filter(error => error.timestamp > cutoffTime)
  }

  clearErrors() {
    this.errors = []
  }

  getErrorSummary() {
    const errors = this.getErrors()
    const summary = {
      total: errors.length,
      byType: {},
      recentCount: this.getRecentErrors(10000).length, // Last 10 seconds
      criticalErrors: errors.filter(e => e.type === 'unhandled_error' || e.type === 'unhandled_promise_rejection').length
    }

    errors.forEach(error => {
      summary.byType[error.type] = (summary.byType[error.type] || 0) + 1
    })

    return summary
  }

  formatErrorsForSubmission() {
    const errors = this.getErrors()
    return errors.map(error => ({
      id: error.id,
      type: error.type,
      message: error.message,
      timestamp: new Date(error.timestamp).toISOString(),
      stack: this.truncateStack(error.stack),
      filename: error.filename,
      line: error.line,
      column: error.column,
      element: error.element,
      source: error.source
    }))
  }

  truncateStack(stack) {
    if (!stack) return null
    const lines = stack.split('\n')
    return lines.slice(0, 10).join('\n') // Limit to first 10 lines
  }

  // Utility method to check if there are critical errors
  hasCriticalErrors() {
    return this.errors.some(error =>
      error.type === 'unhandled_error' ||
      error.type === 'unhandled_promise_rejection'
    )
  }

  // Get errors grouped by similarity
  getGroupedErrors() {
    const groups = {}

    this.errors.forEach(error => {
      const key = `${error.type}:${error.message}`
      if (!groups[key]) {
        groups[key] = {
          ...error,
          count: 1,
          firstSeen: error.timestamp,
          lastSeen: error.timestamp
        }
      } else {
        groups[key].count++
        groups[key].lastSeen = Math.max(groups[key].lastSeen, error.timestamp)
      }
    })

    return Object.values(groups).sort((a, b) => b.lastSeen - a.lastSeen)
  }

  destroy() {
    console.error = this.originalConsoleError
    console.warn = this.originalConsoleWarn
    this.errors = []
  }
}

// Create singleton instance
export const errorTracker = new ErrorTrackerService()
